import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaBookmark } from 'react-icons/fa';
import chefConfig, { slugToKey } from '../chefConfig';
import { supabase } from '../supabase';
import useDarkMode from '../hooks/useDarkMode';
import useLanguage from '../hooks/useLanguage';
import useSocialUser, { notifySocialUserChanged } from '../hooks/useSocialUser';
import usePremium from '../hooks/usePremium';
import useSavedCount from '../hooks/useSavedCount';

const ADMIN_UID = 'c2163866-5b15-4e2a-a2db-61b15771646e';

const InstagramGradientIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <defs>
      <radialGradient id="ig-grad-header-shared" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig-grad-header-shared)" />
    <circle cx="12" cy="12" r="4.6" stroke="white" strokeWidth="1.8" fill="none" />
    <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
  </svg>
);

const YouTubeHeaderIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <rect width="24" height="24" rx="5.5" fill="#FF0000" />
    <path d="M19.6 8.2a2 2 0 0 0-1.4-1.4C16.9 6.5 12 6.5 12 6.5s-4.9 0-6.2.3a2 2 0 0 0-1.4 1.4C4.1 9.5 4.1 12 4.1 12s0 2.5.3 3.8a2 2 0 0 0 1.4 1.4c1.3.3 6.2.3 6.2.3s4.9 0 6.2-.3a2 2 0 0 0 1.4-1.4c.3-1.3.3-3.8.3-3.8s0-2.5-.3-3.8z" fill="white" />
    <path d="M10.2 14.5V9.5l4.2 2.5-4.2 2.5z" fill="#FF0000" />
  </svg>
);

function readCreatorUser() {
  try { return JSON.parse(localStorage.getItem('findish_creator')) || null; }
  catch { return null; }
}

/**
 * 재사용 가능한 헤더. 모든 페이지에서 사용.
 *
 * 기본 동작(모든 콜백 미제공 시):
 *  - 로고: navigate('/')
 *  - Analyze: navigate('/?analyze=1')
 *  - 북마크: navigate('/?tab=saved')
 *  - 언어/다크: localStorage 동기화 (useLanguage/useDarkMode)
 *
 * App.js에선 onLogoClick / onAnalyzeClick / onBookmarkClick / savedCount / isPremium 오버라이드로
 * 검색 리셋·모달 오픈·탭 토글 등 페이지 내부 동작을 유지.
 */
export default function Header({
  onLogoClick,
  onAnalyzeClick,
  onBookmarkClick,
  savedCount: savedCountProp,
  chefProfile: chefProfileProp,
  isPremium: isPremiumProp,
  onOpenLogin,
  socialLoading,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [darkMode, toggleDarkMode] = useDarkMode();
  const [language, setLanguage] = useLanguage();
  const socialUser = useSocialUser();
  const [creatorUser, setCreatorUser] = useState(readCreatorUser);

  useEffect(() => {
    const onCustom = () => setCreatorUser(readCreatorUser());
    const onStorage = (e) => { if (e.key === 'findish_creator') setCreatorUser(readCreatorUser()); };
    window.addEventListener('findish-social-changed', onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('findish-social-changed', onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const hookSlug = params.slug || null;
  const derivedChefKey = hookSlug ? slugToKey[hookSlug] : null;
  const chefProfile = chefProfileProp !== undefined
    ? chefProfileProp
    : (derivedChefKey ? chefConfig[derivedChefKey] : null);

  const { isPremium: fetchedPremium } = usePremium(isPremiumProp === undefined ? socialUser?.uid : null);
  const isPremium = isPremiumProp !== undefined ? isPremiumProp : fetchedPremium;
  const fetchedSavedCount = useSavedCount();
  const savedCount = savedCountProp !== undefined ? savedCountProp : fetchedSavedCount;
  const isAdmin = socialUser?.uid === ADMIN_UID;
  const isOnMyPage = location.pathname === '/mypage';
  const showAnalyze = typeof window !== 'undefined' && window.location.hostname !== 'menu-search.vercel.app';

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  useEffect(() => {
    if (!profileMenuOpen) return;
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setProfileMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileMenuOpen]);

  const doLogoClick = () => {
    if (onLogoClick) onLogoClick();
    else navigate('/');
  };
  const doAnalyzeClick = () => {
    if (onAnalyzeClick) onAnalyzeClick();
    else navigate('/?analyze=1');
  };
  const doBookmarkClick = () => {
    if (onBookmarkClick) onBookmarkClick();
    else navigate('/?tab=saved');
  };

  const doLogout = async () => {
    setProfileMenuOpen(false);
    try { await supabase.auth.signOut(); } catch {}
    try {
      localStorage.removeItem('findish_social');
      localStorage.removeItem('findish_creator');
    } catch {}
    notifySocialUserChanged();
    window.dispatchEvent(new Event('findish-lang-changed'));
    if (location.pathname !== '/') navigate('/');
  };

  const doCreatorLogout = () => {
    try { localStorage.removeItem('findish_creator'); } catch {}
    notifySocialUserChanged();
    if (location.pathname !== '/') navigate('/');
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="header-logo" onClick={doLogoClick}>Findish</button>
      </div>
      <div className="header-right">
        <a href="/creators" className="header-link" onClick={(e) => { e.preventDefault(); navigate('/creators'); }}>
          Creators
        </a>
        <a href="/about" className="header-link" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>
          About
        </a>
        {chefProfile && (
          <>
            <a href={chefProfile.youtubeUrl} target="_blank" rel="noopener noreferrer" className="header-icon-btn" title={`${chefProfile.displayName} 유튜브`}>
              <YouTubeHeaderIcon size={24} />
            </a>
            {chefProfile.instagramUrl && (
              <a href={chefProfile.instagramUrl} target="_blank" rel="noopener noreferrer" className="header-icon-btn" title={`${chefProfile.displayName} 인스타그램`}>
                <InstagramGradientIcon size={24} />
              </a>
            )}
          </>
        )}
        <a href="mailto:ndk68790@gmail.com" style={{ fontSize: '1.1rem', lineHeight: 1 }} title="문의 이메일">✉️</a>
        <a href="https://www.instagram.com/andy__yeyo/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }} title="Findish 인스타그램">
          <InstagramGradientIcon size={18} />
        </a>
        {!chefProfile && !socialUser && (
          <button onClick={() => setLanguage(language === 'kr' ? 'en' : 'kr')} className="dark-toggle">
            {language === 'kr' ? 'EN' : 'KR'}
          </button>
        )}
        {!socialUser && (
          <button onClick={toggleDarkMode} className="dark-toggle">
            {darkMode ? 'Light' : 'Dark'}
          </button>
        )}
        {showAnalyze && (
          <button onClick={doAnalyzeClick} className="dark-toggle">Analyze</button>
        )}
        {(socialUser || creatorUser) && (
          <button className="header-bookmark-btn" onClick={doBookmarkClick} title="저장한 레시피">
            <FaBookmark size={14} />
            {typeof savedCount === 'number' && savedCount > 0 && (
              <span className="header-bookmark-badge">{savedCount}</span>
            )}
          </button>
        )}
        {socialUser ? (
          <>
            {!isPremium && !isOnMyPage && (
              <button
                onClick={() => navigate('/mypage')}
                style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#3182f6', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Premium
              </button>
            )}
            {isPremium && !isOnMyPage && (
              <button
                onClick={() => navigate('/mypage')}
                style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #3182f6', background: 'transparent', color: '#3182f6', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                title="구독 관리"
              >
                ✦ Premium
              </button>
            )}
            <div ref={profileMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setProfileMenuOpen(o => !o)}
                aria-label="프로필 메뉴"
                className="profile-trigger-btn"
              >
                {socialUser.photoURL ? (
                  <img src={socialUser.photoURL} alt="" className="header-profile-img" referrerPolicy="no-referrer" />
                ) : (
                  <span className="profile-trigger-fallback">{(socialUser.name || '?').charAt(0)}</span>
                )}
              </button>
              {profileMenuOpen && (
                <div className={`profile-dropdown${darkMode ? ' dark' : ''}`} role="menu">
                  <div className="profile-dropdown-header">
                    {socialUser.photoURL && (
                      <img src={socialUser.photoURL} alt="" className="profile-dropdown-avatar" referrerPolicy="no-referrer" />
                    )}
                    <div className="profile-dropdown-identity">
                      <div className="profile-dropdown-name">{socialUser.name || '사용자'}</div>
                      {socialUser.email && <div className="profile-dropdown-email">{socialUser.email}</div>}
                    </div>
                  </div>
                  <div className="profile-dropdown-divider" />
                  <button className="profile-dropdown-item" onClick={() => setLanguage(language === 'kr' ? 'en' : 'kr')} role="menuitem">
                    <span>Language</span>
                    <span className="profile-dropdown-value">{language === 'kr' ? '한국어' : 'English'}</span>
                  </button>
                  <button className="profile-dropdown-item" onClick={toggleDarkMode} role="menuitem">
                    <span>Theme</span>
                    <span className="profile-dropdown-value">{darkMode ? 'Dark' : 'Light'}</span>
                  </button>
                  <button className="profile-dropdown-item" onClick={() => { setProfileMenuOpen(false); navigate('/mypage'); }} role="menuitem">
                    <span>마이페이지</span>
                    <span className="profile-dropdown-chevron">›</span>
                  </button>
                  {isAdmin && (
                    <button className="profile-dropdown-item" onClick={() => { setProfileMenuOpen(false); navigate('/admin/settlements'); }} role="menuitem">
                      <span>정산 관리</span>
                      <span className="profile-dropdown-chevron">›</span>
                    </button>
                  )}
                  <div className="profile-dropdown-divider" />
                  <button className="profile-dropdown-item profile-dropdown-item--danger" onClick={doLogout} role="menuitem">
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          </>
        ) : creatorUser ? (
          <>
            <span className="header-creator-name">{creatorUser.username}</span>
            <button onClick={doCreatorLogout} className="header-link">로그아웃</button>
          </>
        ) : socialLoading ? (
          <span className="social-auth-spinner" />
        ) : (
          <button
            onClick={() => (onOpenLogin ? onOpenLogin() : navigate('/?login=1'))}
            className="header-link header-login-btn"
          >
            로그인
          </button>
        )}
      </div>
    </header>
  );
}
